import { useLocation, useNavigate } from "react-router-dom";
import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalePath } from "@/hooks/useLocalePath";
import { stripLangPrefix } from "@/hooks/useLocalePath";

interface HeaderNavLinkProps {
  to: string;
  icon: LucideIcon;
  children: React.ReactNode;
}

export const HeaderNavLink = ({ to, icon: Icon, children }: HeaderNavLinkProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lp = useLocalePath();

  // Get base path for comparison (strip lang prefix from current location)
  const currentBasePath = stripLangPrefix(location.pathname);
  
  // Handle hash links (e.g., /#faq)
  const isHashLink = to.includes('#');
  const [pathname, hash] = to.split('#');
  const targetPath = pathname || '/';
  
  const isActive = isHashLink 
    ? currentBasePath === targetPath && location.hash === `#${hash}`
    : currentBasePath === to;

  const handleClick = (e: React.MouseEvent) => {
    if (isHashLink) {
      e.preventDefault();
      if (currentBasePath === targetPath) {
        // Same page, just scroll to element
        const element = document.getElementById(hash);
        element?.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Navigate to page then scroll
        navigate(lp(targetPath));
        setTimeout(() => {
          const element = document.getElementById(hash);
          element?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={cn(
        "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
        isActive
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
      )}
    >
      <Icon className="w-4 h-4" />
      <span>{children}</span>
    </Link>
  );
};
